/*
    Copyright 2022 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { Span as ApiSpan, Context, SpanContext, isSpanContextValid, trace } from "@opentelemetry/api";
import { TraceState, hrTime } from "@opentelemetry/core";
import { Span } from "@opentelemetry/sdk-trace-base";


import * as SemConvTrace from "../../gen/dynatrace/odin/semconv/v1/SemConvTrace";
import { ComponentLogger } from "../Logger";
import { Fw4Tag } from "../propagator/Fw4Tag";
import { CTIds } from "../Types";
import { getMobileTag, getServerId, getXDtc } from "../utils/context/RumLite";
import { getFw4Tag, getMetaData, getPropagatedResourceAttributes, setFw4Tag, setMetaData, setPropagatedResourceAttributes } from "./SpanEmbedder";
import { SpanMetaData, TransmitOptions } from "./SpanMetaData";

// =============================================================================

export function markSpanPropagatedNow(span: ApiSpan) {
	const metaData = getMetaData(span);
	if (metaData != null) {
		metaData.lastPropagationTime = hrTime();
	}
}

// =============================================================================

/**
 * Prepares the span for export by initializing its SpanMetaData (calculating tenantParentSpanId,
 * setting mobileTag, ...) and creating an FW4Tag if required.
 *
 * @param span the span to prepare for export.
 * @param parentContext the context providing the parent span.
 * @param ids the ids required when creating the FW4 tag for the span.
 * @param transmitOptions the options for initializing the span's meta data.
 */
export function prepareSpanForExport(span: Span, parentContext: Context, ids: CTIds, transmitOptions: TransmitOptions, logger: ComponentLogger): void {
	const metaData = new SpanMetaData(transmitOptions);
	setMetaData(span, metaData);

	const parentSpan = getParent(span, parentContext);
	const { tenantParentSpanId, createFw4Tag, encodedLinkId } = checkParentSpan(parentSpan, logger);

	// set tenant parent spanId and encodedLinkId received via parent
	metaData.tenantParentSpanId = tenantParentSpanId;
	metaData.encodedLinkId = encodedLinkId;

	// propagate resource span attributes from parent to child span
	setPropagatedResourceAttributes(span, getPropagatedResourceAttributes(parentSpan));

	// Create a tag for local propagation as there was none on parent
	if (createFw4Tag) {
		createTagOnTraceState(span.spanContext(), ids);
	}

	// set the serverId if provided and not yet set
	const fw4Tag = getFw4Tag(span.spanContext().traceState);
	if (fw4Tag?.serverId === 0) {
		const serverId = getServerId(parentContext);
		if (serverId != null) {
			fw4Tag.serverId = serverId;
			metaData.mobileTag = getMobileTag(parentContext);
			const xDtc = getXDtc(parentContext);
			if (xDtc != null) {
				span.setAttribute(SemConvTrace.DT_RUM_DTC, xDtc);
			}
		}
	}
}

// ----------------------------------------------------------------------------
export function addStacktrace(key: string, span: Span, logger: ComponentLogger): void {
	// remove error message and first frame ("at addStacktrace") from the stack
	const stackObj: { stack?: string } = {};
	Error.captureStackTrace(stackObj, addStacktrace);
	const stack = stackObj.stack!.substring(stackObj.stack!.indexOf("\n") + 1);

	if (typeof(stack) === "string") {
		span.setAttribute(key, stack);
		if (logger.debugEnabled) {
			logger.debug(`${key}=${stack}`);
		}
	}
}

// ----------------------------------------------------------------------------
function createTagOnTraceState(spanContext: SpanContext, ids: CTIds): void {
	if (spanContext.traceState == null) {
		spanContext.traceState = new TraceState();
	}
	// attach an FW4 tag on TraceState which is automatically propagated to local child spans
	const tag = new Fw4Tag(ids, spanContext).setRootPathRandom();
	tag.extensions.createNewTcr();
	setFw4Tag(spanContext.traceState, tag);
}

// ----------------------------------------------------------------------------
function checkParentSpan(parentSpan: ApiSpan | undefined, logger: ComponentLogger): { tenantParentSpanId: string | undefined; encodedLinkId: number | undefined; createFw4Tag: boolean } {
	let tenantParentSpanId: string | undefined;
	let encodedLinkId: number | undefined;
	let createFw4Tag = true;

	if (parentSpan == null) {
		return { tenantParentSpanId, createFw4Tag, encodedLinkId };
	}

	const spanContext = parentSpan.spanContext();
	if (spanContext.isRemote) {
		if (spanContext.traceState != null) {
			const fw4Tag = getFw4Tag(spanContext.traceState);
			if (fw4Tag != null) {
				createFw4Tag = false;

				// spanId received via TraceState to allow exporting it
				if (fw4Tag.extensions.spanId.length > 0) {
					tenantParentSpanId = fw4Tag.extensions.spanId;
				}

				// encoded linkId received from FW4 tag
				encodedLinkId = fw4Tag.encodedLinkId;
			}
		}
	} else {
		// for local parents use the parent spanId also as tenantParentSpanId
		tenantParentSpanId = spanContext.spanId;

		// Set/update last-propagation-time on parent
		markSpanPropagatedNow(parentSpan);

		// local parent, should have a TraceState with tag attached already...
		if (getFw4Tag(spanContext.traceState) != null) {
			createFw4Tag = false;
		} else {
			logger.warn(`Expected FW4 tag to exist for span (traceId: ${spanContext.traceId}, spanId: ${spanContext.spanId}) but none found.`);
		}
	}

	return { tenantParentSpanId, createFw4Tag, encodedLinkId };
}

// ----------------------------------------------------------------------------
function getParent(span: Span, parentContext: Context): ApiSpan | undefined {
	const parentSpan = trace.getSpan(parentContext);
	// the parentContext passed to the span processor in the tracer might not hold the actual
	// parent span, e.g. when SpanOptions.root = true was used. So check for this here.
	if (parentSpan == null || parentSpan.spanContext().spanId !== span.parentSpanContext?.spanId || !isSpanContextValid(parentSpan.spanContext())) {
		return undefined;
	}
	return parentSpan;
}
